/**
 * letter-columns.tsx — wspólna konfiguracja KOLUMN listy pism.
 *
 * Jedno źródło prawdy dla dwóch ekranów, które pokazują pisma jako tabelę z kolumnami „jak na
 * liście": strony `Letters` oraz sekcji kokpitu decyzji (`pages/processes/decision-sections`,
 * „Grupuj według: Kartoteka"). Zmiana tutaj jest widoczna na obu — kolumny nie mogą się rozjechać.
 *
 * „Nadawca" (kolumna `sender`, nazwa buforowana `counterparty_name_cached` + NIP) NIE jest polem
 * katalogu — `documents.contractor_id`/`counterparty_*_cached` nie mają wpisu w
 * `system_module_fields` dla `letters` (pisma czytają stronę główną z `document_parties`, katalog
 * jej nie modeluje, w odróżnieniu od faktur, gdzie `vendor_id` JEST polem core). Zostaje więc osobną
 * kolumną (`buildLetterSenderColumn`), POZA `buildCoreColumns` i POZA rejestrem nadpisań — sort i
 * `?columns=` remapowane ręcznie (`LETTERS_SORT_SLUG_OVERRIDES`/`LETTER_COLUMN_ID_TO_SLUG`), tak jak
 * „Firma" (`buildCompanyColumn`). Pisma są bare-on-core (brak satelity).
 *
 * Zawartość: rejestr nadpisań renderu kolumn rdzennych (`LETTERS_OVERRIDES`), slugi obsługiwane
 * osobno (`LETTERS_EXCLUDE_SLUGS`), remap sortu (`LETTERS_SORT_SLUG_OVERRIDES`), kolumna nadawcy
 * (`buildLetterSenderColumn`), slugi dołączane do `?columns=` (`LETTER_ALWAYS_SLUGS`,
 * `LETTER_COLUMN_COMPANIONS`), mapa id kolumny → slug backendu (`LETTER_COLUMN_ID_TO_SLUG`),
 * prefiks kluczy i18n kolumn procesu (`LETTER_PROCESS_LABEL_PREFIX`) i typ wiersza listy
 * (`LetterRow`). Kolumny rdzenne/dodatkowe buduje katalog pól (`buildCoreColumns`/
 * `buildExtColumns` z `ext-list-columns`); kolumny procesu — wspólne `process-columns.tsx`.
 */

import { DOCUMENT_URGENCIES, LETTER_STATUSES } from '@saas/shared';

import type { ColumnDefinition } from '@/components/configurable-table';
import { letterStatusLabel, letterUrgencyLabel } from '@/lib/documents/document-select-labels';
import { PROCESS_COLUMN_ID_TO_SLUG } from '@/lib/documents/process-columns';
import {
  buildStaticSelectOverride,
  type ColumnCompanionRule,
  type CoreColumnOverride,
} from '@/lib/ext-list-columns';

/** Prefiks kluczy i18n kolumn procesu pism (patrz `buildProcessColumns`). */
export const LETTER_PROCESS_LABEL_PREFIX = 'tenant.documents.letters';

/**
 * Slugi rdzenne obsługiwane OSOBNO (dedykowane budowniczowie z filtrami/renderem, którego
 * generyk `buildCoreColumns` nie odtworzy), więc pomijane przez `buildCoreColumns` — inaczej
 * powstałyby dwie kolumny na to samo pole. Mirror `PI_EXCLUDE_SLUGS` w
 * `lib/documents/purchase-invoice-columns.tsx`:
 * firma/opiekun dedykowanymi budowniczymi (`buildCompanyColumn`/`buildDocumentCoreRefColumns`).
 * `source` WYŁĄCZONY też — pisma renderują je przez `buildDocumentCoreRefColumns({ withSource:
 * true })` (mapowanie i18n `DOC_SOURCE_VALUES` WSPÓLNE dla 4 klas STANDARD dokumentów), nie
 * przez generyk/override tej strony.
 */
export const LETTERS_EXCLUDE_SLUGS: ReadonlySet<string> = new Set([
  'company_id',
  'owner_user_id',
  'source',
]);

/**
 * Nadpisanie slug→slug-sortu dla `?sort=` (reszta: id kolumny = slug, fallback w
 * `useServerListControls`). „Nadawca" (kolumna `sender`, POZA katalogiem — patrz nagłówek
 * pliku) sortuje się po buforowanej NAZWIE (`counterparty_name_cached`), nie po `sender`
 * (nieistniejący slug backendu).
 */
export const LETTERS_SORT_SLUG_OVERRIDES: Readonly<Record<string, string>> = {
  sender: 'counterparty_name_cached',
};

/**
 * Slugi dołączane do `?columns=` ZAWSZE, niezależnie od widocznych kolumn: `gcrecord` — czyta go
 * akcja wiersza (restore/delete), nie jest kolumną.
 */
export const LETTER_ALWAYS_SLUGS: readonly string[] = ['gcrecord'];

/** Reguły towarzysza dla `?columns=`: „Nadawca" (composite) czyta nazwę i NIP. */
export const LETTER_COLUMN_COMPANIONS: readonly ColumnCompanionRule[] = [
  { whenSlug: 'counterparty_name_cached', addSlug: 'counterparty_tax_id_cached' },
];

/**
 * Mapa id kolumny → slug backendu dla `?columns=` — część STAŁA: firma/opiekun/kategoria/źródło/
 * nadawca/proces dopisane ręcznie (dedykowane buildery, nie `buildCoreColumns`). Kolumny z katalogu
 * (`buildCoreColumns`) dopisuje wołający pętlą `id = slug`.
 */
export const LETTER_COLUMN_ID_TO_SLUG: Readonly<Record<string, string>> = {
  company: 'company_id',
  category: 'category',
  owner_user_id: 'owner_user_id',
  source: 'source',
  sender: 'counterparty_name_cached',
  ...PROCESS_COLUMN_ID_TO_SLUG,
};

/**
 * Rejestr nadpisań kolumn rdzennych pism (per-kartoteka) — mirror
 * `PURCHASE_INVOICE_OVERRIDES` w `lib/documents/purchase-invoice-columns.tsx`. Sortowalność bierze z
 * `ctx.resolveSortable(ctx.field)` (ta sama serwerowa reguła co generyk). Klucze = slugi pól
 * z katalogu (`system_module_fields.slug`). `status`/`urgency` to zamknięte enumy pism (własne,
 * z formularza pisma) — `buildStaticSelectOverride`: lista wyboru w wierszu filtrów i etykieta w
 * komórce z jednej funkcji (`document-select-labels`). `document_date` NIE jest nadpisana —
 * renderuje ją generyk (`renderExtFieldValue`: format daty pl-PL).
 */
export const LETTERS_OVERRIDES: Readonly<Record<string, CoreColumnOverride<LetterRow>>> = {
  system_number: ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 160,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'text', fieldSlug: 'system_number' },
    renderCell: (value) => (
      <span className="font-mono text-xs text-text-secondary">{value ? String(value) : '—'}</span>
    ),
  }),
  title: ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 280,
    hideable: false,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'text', fieldSlug: 'title' },
    renderCell: (value) => (
      <span className="font-medium text-text-primary">{value ? String(value) : '—'}</span>
    ),
  }),
  status: buildStaticSelectOverride<LetterRow>({
    codes: LETTER_STATUSES,
    labelOf: letterStatusLabel,
  }),
  urgency: buildStaticSelectOverride<LetterRow>({
    codes: DOCUMENT_URGENCIES,
    labelOf: letterUrgencyLabel,
  }),
};

/** Slug backendu, po którym filtruje się „Nadawca" — nazwa buforowana (ten sam co w remapie sortu). */
const LETTER_SENDER_FILTER_SLUG = 'counterparty_name_cached';

/**
 * „Nadawca" — POZA katalogiem pól (patrz nagłówek pliku), więc poza `buildCoreColumns` i poza
 * rejestrem nadpisań; composite nazwa+NIP z buforu `counterparty_*_cached` (towarzysz NIP-u
 * dołączany do projekcji regułą `LETTER_COLUMN_COMPANIONS`).
 *
 * `isSortable` — serwerowa sortowalność kolumny systemowej (`meta.sortable` ostatniej odpowiedzi;
 * id `sender` remapuje na slug sortu `LETTERS_SORT_SLUG_OVERRIDES`). Kolumna jest typowana wierszem
 * listy pism (`LetterRow`) — tym samym wierszem buduje ją strona listy i sekcja kokpitu decyzji
 * (rzut na wspólny typ sekcji robi dopiero rejestr zestawów).
 *
 * `isFilterable` — serwerowa filtrowalność po SLUGU BACKENDU (`meta.filterable` ostatniej odpowiedzi,
 * bez remapu — tu `counterparty_name_cached`, ten sam co dla sortu). Gdy `true`: filtr tekstowy po
 * nazwie nadawcy w wierszu filtrów (warunek „zawiera"). Pominięte/`false`: komórka filtra pusta
 * (`filterable: false`) — kolumna bez filtra nie ma martwego pola „Szukaj…".
 */
export function buildLetterSenderColumn(args: {
  t: (key: string) => string;
  isSortable: (slug: string) => boolean;
  isFilterable?: (slug: string) => boolean;
}): ColumnDefinition<LetterRow> {
  const { t, isSortable, isFilterable } = args;
  return {
    id: 'sender',
    accessor: 'counterpartyNameCached',
    header: t('tenant.documents.letters.columns.sender'),
    // Slug pola backendu (id ≠ slug) niezależny od filtra — kalendarz listy dopasowuje po nim.
    fieldSlug: LETTER_SENDER_FILTER_SLUG,
    defaultVisible: true,
    defaultWidth: 240,
    sortable: isSortable('sender'),
    filterable: false,
    ...(isFilterable?.(LETTER_SENDER_FILTER_SLUG)
      ? { headerFilter: { category: 'text' as const, fieldSlug: LETTER_SENDER_FILTER_SLUG } }
      : {}),
    renderCell: (_value, row) => (
      <div className="text-text-primary">
        {row.counterpartyNameCached ?? '—'}
        {row.counterpartyTaxIdCached ? (
          <span className="ml-2 text-xs text-text-muted">NIP {row.counterpartyTaxIdCached}</span>
        ) : null}
      </div>
    ),
  };
}

/**
 * `id`, `gcrecord` (zawsze w `alwaysSlugs`) i `title` (kolumna `hideable: false`) są jedynymi polami
 * GWARANTOWANYMI w każdej odpowiedzi — reszta zależy od tego, co user aktualnie widzi (Kreator
 * Listy może schować każdą inną kolumnę, a backend w trybie projekcji `?columns=` zwraca WYŁĄCZNIE
 * zażądane pola). Typ wiersza listy pism; wiersz sekcji kokpitu decyzji = ten wiersz + kroki decyzji.
 */
export interface LetterRow {
  id: string;
  companyId?: string;
  systemNumber?: string | null;
  title: string | null;
  status?: string;
  urgency?: string;
  description?: string | null;
  notes?: string | null;
  rejectionReason?: string | null;
  ownerUserId?: string | null;
  source?: string;
  externalId?: string | null;
  externalSource?: string | null;
  documentDate?: string | null;
  counterpartyNameCached?: string | null;
  counterpartyTaxIdCached?: string | null;
  contractorId?: string | null;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze) — kolumna „Kategoria". */
  categoryName?: string | null;
  gcrecord: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdByUserId?: string;
  updatedByUserId?: string;
  /**
   * Kolumny przekrojowe silnika list (`apps/api/lib/list-engine/providers/process-columns.ts`)
   * — DOKŁADANE na każdym wierszu gdy tenant ma aktywny moduł `processes`, POMIJANE
   * całkowicie (klucz nieobecny) gdy moduł nieaktywny. `currentStage` to bieżące etapy sprawy
   * w toku (0..N, gałęzie równoległe) albo nazwa etapu końcowego sprawy zakończonej (jeden element).
   */
  activeProcessName?: string | null;
  currentStage?: string[];
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie
   * projekcji `?columns=` i zażądano ≥1 aktywnego pola ext (kontrakt jak
   * `GET /api/products?columns=` / `GET /api/contractors?columns=`).
   */
  ext?: Record<string, unknown>;
}
